import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Copy, Printer, Package, MapPin, Truck, Mail, Phone, MessageSquare, Clock, Calendar, ArrowRight, Plane, Building2, Home, ExternalLink } from 'lucide-react';
import { Button } from './ui/Button';
interface PaymentSuccessPageProps {
  orderSummary?: {
    total: number;
    packageCount: number;
    route: string;
    packageType: string;
  };
  onTrackShipment?: () => void;
  onShipAnother?: () => void;
  onGoHome?: () => void;
}
export function PaymentSuccessPage({
  orderSummary = {
    total: 127.5,
    packageCount: 2,
    route: 'New York, USA → Lagos, Nigeria',
    packageType: 'Electronics'
  },
  onTrackShipment,
  onShipAnother,
  onGoHome
}: PaymentSuccessPageProps) {
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  // Generate a tracking ID
  const trackingId = `LGS-US-${Date.now().toString().slice(-9)}`;
  // Estimated dates
  const today = new Date();
  const estimatedDelivery = new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000);
  const formattedDelivery = estimatedDelivery.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);
  const handleCopyTrackingId = () => {
    navigator.clipboard.writeText(trackingId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handlePrint = () => {
    window.print();
  };
  const timelineSteps = [{
    icon: Package,
    title: 'Order Confirmed',
    description: 'Your payment has been processed',
    time: 'Just now'
  }, {
    icon: Truck,
    title: 'Drop Off Package',
    description: 'Bring your package to our US location',
    time: 'Within 3 days'
  }, {
    icon: Plane,
    title: 'In Transit',
    description: 'Package shipped to Nigeria',
    time: '3-7 days'
  }, {
    icon: Building2,
    title: 'Customs Clearance',
    description: 'Package clears Nigerian customs',
    time: '7-10 days'
  }, {
    icon: Home,
    title: 'Delivery',
    description: 'Package delivered to recipient',
    time: '10-14 days'
  }];
  return <div className="min-h-screen bg-gradient-to-b from-success-light/30 to-gray-50 py-8 sm:py-12" data-id="element-546">
      <div className="max-w-3xl mx-auto px-4" data-id="element-547">
        {/* Success Animation */}
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15
      }} className="text-center mb-8" data-id="element-548">
          <div className="relative inline-block" data-id="element-549">
            {/* Animated rings */}
            <motion.div initial={{
            scale: 0.8,
            opacity: 1
          }} animate={{
            scale: 2,
            opacity: 0
          }} transition={{
            duration: 1,
            repeat: showConfetti ? Infinity : 0,
            repeatDelay: 0.5
          }} className="absolute inset-0 rounded-full bg-success/20" data-id="element-550" />
            <motion.div initial={{
            scale: 0.8,
            opacity: 1
          }} animate={{
            scale: 1.5,
            opacity: 0
          }} transition={{
            duration: 1,
            delay: 0.2,
            repeat: showConfetti ? Infinity : 0,
            repeatDelay: 0.5
          }} className="absolute inset-0 rounded-full bg-success/30" data-id="element-551" />

            {/* Checkmark circle */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="relative w-24 h-24 bg-success rounded-full flex items-center justify-center shadow-lg shadow-success/30" data-id="element-552">
              <motion.div initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              delay: 0.5,
              duration: 0.5
            }} data-id="element-553">
                <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-554" />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Success Message */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="text-center mb-8" data-id="element-555">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" data-id="element-556">
            Payment Successful!
          </h1>
          <p className="text-gray-600 text-lg" data-id="element-557">
            Your shipment has been confirmed and is ready for processing.
          </p>
        </motion.div>

        {/* Tracking ID Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm mb-6" data-id="element-558">
          <div className="text-center" data-id="element-559">
            <p className="text-sm text-gray-500 mb-2" data-id="element-560">Your Tracking ID</p>
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-561">
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl px-6 py-4" data-id="element-562">
                <span className="text-2xl sm:text-3xl font-mono font-bold text-brand tracking-wider" data-id="element-563">
                  {trackingId}
                </span>
              </div>
              <button onClick={handleCopyTrackingId} className={`p-3 rounded-xl transition-all ${copied ? 'bg-success text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title="Copy tracking ID" data-id="element-564">
                {copied ? <CheckCircle className="w-5 h-5" data-id="element-565" /> : <Copy className="w-5 h-5" data-id="element-566" />}
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 text-success bg-success-light px-4 py-2 rounded-full inline-flex" data-id="element-567">
              <Mail className="w-4 h-4" data-id="element-568" />
              <span className="text-sm font-medium" data-id="element-569">
                Tracking ID has been sent to your email
              </span>
            </div>
          </div>
        </motion.div>

        {/* Shipment Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm mb-6" data-id="element-570">
          <div className="bg-secondary-900 p-4 text-white" data-id="element-571">
            <h3 className="font-semibold flex items-center gap-2" data-id="element-572">
              <Package className="w-5 h-5" data-id="element-573" />
              Shipment Summary
            </h3>
          </div>

          <div className="p-6" data-id="element-574">
            <div className="grid grid-cols-2 gap-6" data-id="element-575">
              <div data-id="element-576">
                <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-577">
                  Route
                </span>
                <span className="text-gray-900 font-medium flex items-center gap-2" data-id="element-578">
                  <MapPin className="w-4 h-4 text-brand" data-id="element-579" />
                  {orderSummary.route}
                </span>
              </div>
              <div data-id="element-580">
                <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-581">
                  Package Type
                </span>
                <span className="text-gray-900 font-medium" data-id="element-582">
                  {orderSummary.packageType}
                </span>
              </div>
              <div data-id="element-583">
                <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-584">
                  Packages
                </span>
                <span className="text-gray-900 font-medium" data-id="element-585">
                  {orderSummary.packageCount} package(s)
                </span>
              </div>
              <div data-id="element-586">
                <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-587">
                  Total Paid
                </span>
                <span className="text-2xl font-bold text-brand" data-id="element-588">
                  ${orderSummary.total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-3" data-id="element-589">
              <Calendar className="w-5 h-5 text-brand" data-id="element-590" />
              <div data-id="element-591">
                <span className="text-sm text-gray-500" data-id="element-592">
                  Estimated Delivery
                </span>
                <span className="font-semibold text-gray-900 block" data-id="element-593">
                  {formattedDelivery}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-id="element-594">
          <Button onClick={onTrackShipment} className="gap-2" data-id="element-595">
            <Truck className="w-4 h-4" data-id="element-596" />
            Track My Shipment
          </Button>
          <Button variant="outline" onClick={handlePrint} className="gap-2" data-id="element-597">
            <Printer className="w-4 h-4" data-id="element-598" />
            Print Receipt
          </Button>
          <Button variant="outline" onClick={onShipAnother} className="gap-2" data-id="element-599">
            <Package className="w-4 h-4" data-id="element-600" />
            Ship Another Package
          </Button>
        </motion.div>

        {/* What Happens Next */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-6" data-id="element-601">
          <h3 className="font-semibold text-gray-900 mb-6 flex items-center gap-2" data-id="element-602">
            <Clock className="w-5 h-5 text-brand" data-id="element-603" />
            What Happens Next
          </h3>

          <div className="space-y-0" data-id="element-604">
            {timelineSteps.map((step, index) => <div key={index} className="flex gap-4" data-id="element-605">
                <div className="flex flex-col items-center" data-id="element-606">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${index === 0 ? 'bg-success text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-607">
                    <step.icon className="w-5 h-5" data-id="element-608" />
                  </div>
                  {index < timelineSteps.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-609" />}
                </div>
                <div className="flex-1 pb-6" data-id="element-610">
                  <div className="flex items-center justify-between" data-id="element-611">
                    <h4 className={`font-medium ${index === 0 ? 'text-success' : 'text-gray-900'}`} data-id="element-612">
                      {step.title}
                    </h4>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full" data-id="element-613">
                      {step.time}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-614">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Email Confirmation Notice */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-brand-50 border border-brand-100 rounded-xl p-4 mb-6" data-id="element-615">
          <div className="flex items-start gap-3" data-id="element-616">
            <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-617">
              <Mail className="w-5 h-5 text-brand" data-id="element-618" />
            </div>
            <div data-id="element-619">
              <p className="font-medium text-brand-800" data-id="element-620">
                Confirmation Email Sent
              </p>
              <p className="text-sm text-brand-600 mt-1" data-id="element-621">
                We've sent a detailed receipt and shipping instructions to your
                email address. Please check your inbox (and spam folder) for the
                confirmation.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Customer Support */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.9
      }} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm" data-id="element-622">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-623">
            <MessageSquare className="w-5 h-5 text-brand" data-id="element-624" />
            Need Help?
          </h3>

          <p className="text-gray-600 text-sm mb-4" data-id="element-625">
            Our customer support team is available 24/7 to assist you with any
            questions about your shipment.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-626">
            <a href="tel:+1234567890" className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-627">
              <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center" data-id="element-628">
                <Phone className="w-5 h-5 text-brand" data-id="element-629" />
              </div>
              <div data-id="element-630">
                <span className="text-xs text-gray-400 block" data-id="element-631">Call Us</span>
                <span className="font-medium text-gray-900" data-id="element-632">
                  +1 (234) 567-890
                </span>
              </div>
            </a>

            <a href="mailto:support@taskrunner.com" className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-633">
              <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center" data-id="element-634">
                <Mail className="w-5 h-5 text-brand" data-id="element-635" />
              </div>
              <div data-id="element-636">
                <span className="text-xs text-gray-400 block" data-id="element-637">Email Us</span>
                <span className="font-medium text-gray-900" data-id="element-638">
                  support@taskrunner.com
                </span>
              </div>
            </a>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-639">
            <Button variant="outline" size="sm" className="gap-2" data-id="element-640">
              <MessageSquare className="w-4 h-4" data-id="element-641" />
              Start Live Chat
            </Button>
          </div>
        </motion.div>

        {/* Back to Home */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="text-center mt-8" data-id="element-642">
          <button onClick={onGoHome} className="text-gray-500 hover:text-brand text-sm transition-colors inline-flex items-center gap-1" data-id="element-643">
            Return to Homepage
            <ArrowRight className="w-4 h-4" data-id="element-644" />
          </button>
        </motion.div>
      </div>
    </div>;
}